import { TIMEZONE_OPTIONS } from '../constants/timezones';
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../hooks/useCanWriteCompanySettings';
import { unwrap } from '../lib/api-envelope';
import { useBack } from '../hooks/useBack';
import PushDeviceCard from '../components/push/PushDeviceCard';
import { deviceKind } from '../lib/push';

/*
 * `verticals`: the event is listed only for a tenant with one of these. Each
 * list is WHO EMITS IT in the API, traced 2026-10-08, not a guess from the name:
 *   job_assigned, job_starting_tomorrow, job_status_change, daily_job_alert,
 *   call_sheet, casing_spec_corrected: all emitted from casing jobs
 *     (dispatch, job-status-notify, daily-job-alerts, call-sheet, the casing
 *     correction notifier). A pressure-testing tech can never receive one.
 *   ticket_customer_approved / _rejected: the casing ticket approval door AND
 *     the downhole one (lib/downhole-ticket-approval.ts).
 *   pt_calibration_due: the PT calibration reminder service only.
 * An event with no `verticals` (email_delivery_failed) is for every tenant.
 * Positive guards only: a multi-vertical tenant sees the union.
 */
const CASING = ['casing_running'] as const;
const CUSTOMER_APPROVED_TICKETS = ['casing_running', 'downhole_tools'] as const;

const EVENT_TYPES = [
  { key: 'job_assigned', label: 'Job Assigned', description: 'When you are assigned to a new job', verticals: CASING },
  { key: 'job_starting_tomorrow', label: 'Shift Reminder', description: 'Reminder the day before a job starts', verticals: CASING },
  { key: 'job_status_change', label: 'Job Status Change', description: 'When a job status is updated', verticals: CASING },
  { key: 'daily_job_alert', label: 'Daily Job Alert', description: 'Daily summary of unstarted jobs', verticals: CASING },
  // No push: the call sheet IS the text/email (the API sends it through no push path).
  { key: 'call_sheet', label: 'Call Sheet', description: 'The call sheet for a job you are crewed on', channels: ['sms', 'email'], verticals: CASING },
  // A job chat message (fieldiq-api job-chat-notify): push, SMS and in-app,
  // all default ON (api#1310). To the job's crew and the office; never the
  // sender or anyone with the chat open. SMS is the push-then-SMS fallback
  // (sent only where no push lands); in-app is the bell row, which the API
  // writes for office roles only.
  // `channelRoles` narrows ONE column by role: the bell row goes to
  // FEED_RECIPIENT_ROLES only (fieldiq-api notification.service.ts), so a field
  // hand's in-app toggle would be read by nothing.
  {
    key: 'job_chat_message',
    label: 'Job chat',
    description: 'A new message in the chat of a job you are on',
    channels: ['push', 'sms', 'in_app'],
    channelRoles: { in_app: ['admin', 'office_staff', 'supervisor', 'dispatcher'] },
    verticals: CASING,
  },
  // Still clocked in after 14 hours (fieldiq-api clock-out-watch): push only, default ON.
  { key: 'clock_out_reminder', label: 'Still clocked in', description: 'A reminder when you have been clocked in for over 14 hours', channels: ['push'] },
  // A customer's decision through the magic link, emailed to the office.
  // Email only: the backend map (notification-gate DEFAULT_ENABLED) has no
  // sms entry for either, so an SMS toggle here would be a control that does
  // nothing. `channels` narrows the row; an event without it gets every column.
  { key: 'ticket_customer_approved', label: 'Customer approved a ticket', description: 'When a customer signs off on a ticket', channels: ['email'], verticals: CUSTOMER_APPROVED_TICKETS },
  { key: 'ticket_customer_rejected', label: 'Customer rejected a ticket', description: 'When a customer rejects a ticket', channels: ['email'], verticals: CUSTOMER_APPROVED_TICKETS },
  // An email address went bad — a hard bounce, a suppressed send, a spam
  // complaint — once per address per tenant, from the Resend webhook
  // (fieldiq-api 0383). In-app is the primary channel and defaults ON; email
  // is the "delivery problems" section of the daily digest and defaults OFF,
  // mirroring the customer-decision rows above. Recipients are admin and
  // office_staff only, so `roles` hides the row from everyone else — a
  // dispatcher's stored preference would never be read.
  {
    key: 'email_delivery_failed',
    label: 'Email address bouncing',
    description: 'When a customer or crew email address starts bouncing. In-app right away; the daily digest by email if you turn it on',
    channels: ['in_app', 'email'],
    roles: ['admin', 'office_staff'],
  },
  // A field crew corrected a casing spec the office booked (fieldiq-api 0400).
  // In-app only, default ON, mirroring the backend map. Recipients are the
  // roles that can edit a job — admin, office_staff, supervisor, dispatcher —
  // so `roles` hides the row from a field worker, whose stored preference would
  // never be read.
  {
    key: 'casing_spec_corrected',
    label: 'Casing spec corrected in the field',
    description: 'When a crew corrects the pipe size, weight, grade or connection on a job',
    channels: ['in_app'],
    roles: ['admin', 'office_staff', 'supervisor', 'dispatcher'],
    verticals: CASING,
  },
  // A pressure-test instrument reaching a calibration warn tier or overdue
  // (fieldiq-api 0467). In-app ON, email OFF, mirroring the backend map.
  // Recipients are PT_CALIBRATE_ROLES — admin, office_staff, supervisor.
  {
    key: 'pt_calibration_due',
    label: 'Instrument calibration due',
    description: 'When a pressure-testing gauge or recorder is coming due for calibration, or is overdue',
    channels: ['in_app', 'email'],
    roles: ['admin', 'office_staff', 'supervisor'],
    verticals: ['pressure_testing'],
  },
  // The shared radiation module (fieldiq-api#1334, RAD-1). No `verticals`:
  // the module has no vertical guard, so any tenant holding a source gets these.
  // A source recorded lost in hole goes to the tenant's admins at once
  // (radiation-notify.service.ts); BOTH channels default ON, mirroring the
  // backend map, because it starts the tenant's regulatory reporting chain.
  {
    key: 'radiation_source_lost',
    label: 'Radioactive source lost in hole',
    description: 'When a sealed source is recorded lost in hole, with the depth it was lost at',
    channels: ['in_app', 'email'],
    roles: ['admin'],
  },
  // A source's leak test reaching a warning day or overdue
  // (radiation-leak-test-sweep.service.ts), to admin + office_staff. In-app ON,
  // email OFF, mirroring the backend map — the pt_calibration_due precedent.
  {
    key: 'radiation_leak_test_due',
    label: 'Source leak test due',
    description: 'When a radioactive source is coming due for its leak test, or is overdue',
    channels: ['in_app', 'email'],
    roles: ['admin', 'office_staff'],
  },
] as const;

type EventType = (typeof EVENT_TYPES)[number];

/**
 * A channel listed here is offered ONLY to events that name it in `channels`.
 * Nothing gates in-app delivery for the older events (they are written
 * unconditionally), so an in-app toggle on those rows would be a control that
 * does nothing — the same lie HIDDEN_CHANNELS exists to prevent for push.
 */
const EXPLICIT_ONLY_CHANNELS: readonly string[] = ['in_app'];

function eventHasChannel(event: EventType, channel: string, role?: string): boolean {
  if ('channelRoles' in event) {
    const roles = (event.channelRoles as Record<string, readonly string[]>)[channel];
    if (roles && !roles.includes(role ?? '')) return false;
  }
  if ('channels' in event) return (event.channels as readonly string[]).includes(channel);
  return !EXPLICIT_ONLY_CHANNELS.includes(channel);
}

function eventVisibleToVerticals(event: EventType, hasVertical: (v: string) => boolean): boolean {
  return !('verticals' in event) || (event.verticals as readonly string[]).some((v) => hasVertical(v));
}

function eventVisibleToRole(event: EventType, role: string | undefined): boolean {
  return !('roles' in event) || (event.roles as readonly string[]).includes(role ?? '');
}

const CHANNELS = [
  { key: 'in_app', label: 'In-app' },
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
  { key: 'push', label: 'Push' },
] as const;

/**
 * Channels hidden from the UI because nothing delivers them. Empty since
 * fieldiq-api 0524 + push-then-SMS: push is delivered for job assigned, shift
 * reminder, job status, daily alert, job chat and the clock-out reminder, and
 * where a push lands the text for the same event is not sent.
 */
const HIDDEN_CHANNELS: readonly string[] = [];

/**
 * Events hidden from field workers and drivers.
 *
 * `daily_job_alert`: the alert's recipient query in daily-job-alerts.ts selects
 * `role IN ('admin', 'dispatcher', 'office_staff')`, so a field worker or driver
 * who enables it receives nothing regardless of the stored preference.
 */
const FIELD_WORKER_HIDDEN_EVENTS: readonly string[] = [
  'daily_job_alert',
  // The recipient query in customer-decision-notify.service.ts selects
  // admin + office_staff only, so a field worker's stored preference is never
  // read. Same lie as daily_job_alert, same fix.
  'ticket_customer_approved',
  'ticket_customer_rejected',
];

/**
 * Default enabled state per event×channel — mirrors the backend canNotify gate.
 * When no stored preference row exists, these are the effective defaults.
 */
const EVENT_DEFAULTS: Record<string, Record<string, boolean>> = {
  // Push ON where SMS is ON (fieldiq-api push-then-SMS); job_status_change and
  // daily_job_alert stay OFF on every channel until the person turns them on.
  job_assigned:          { sms: true,  push: true,  email: false },
  job_starting_tomorrow: { sms: true,  push: true,  email: false },
  job_status_change:     { sms: false, push: false, email: false },
  daily_job_alert:       { sms: false, push: false, email: false },
  // BOTH true, mirroring the backend map. Call sheets were sent to everyone
  // with a phone or an email until fieldiq-api #440, so anything but `true`
  // here would misdescribe the state a user with no saved row is actually in.
  call_sheet:            { sms: true,  email: true },
  // All three ON, mirroring the backend map (notification-gate, api#1310).
  job_chat_message:      { push: true, sms: true, in_app: true },
  clock_out_reminder:    { push: true },
  // Both OFF, mirroring the backend: a new mail nobody has received must send
  // nothing until a user turns it on. No sms key — nothing texts these.
  ticket_customer_approved: { email: false },
  ticket_customer_rejected: { email: false },
  // In-app ON, email OFF — mirroring the backend map exactly.
  email_delivery_failed:    { in_app: true, email: false },
  // In-app ON — mirroring the backend map exactly.
  casing_spec_corrected:    { in_app: true },
  // In-app ON, email OFF — mirroring the backend map exactly.
  pt_calibration_due:       { in_app: true, email: false },
  // Both ON for a lost source, in-app only for a leak test due — mirroring the
  // backend map exactly (notification-gate, api#1334).
  radiation_source_lost:    { in_app: true, email: true },
  radiation_leak_test_due:  { in_app: true, email: false },
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function formatHour(h: number): string {
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  return `${h - 12}:00 PM`;
}

type PrefMap = Record<string, Record<string, boolean>>;

export default function NotificationPreferencesScreen() {
  const goBack = useBack('/field');
  const { pathname } = useLocation();

  /**
   * This screen is mounted TWICE, in two route trees, for two user classes:
   * `/field/settings/notifications` for a casing field worker, and
   * `/settings/notifications` inside the office layout.
   *
   * The field tree has no bottom tab bar — the home screen's tiles are the
   * whole of its navigation — so a field hand's back control has to land on
   * /field rather than wherever history happens to point. The office copy has
   * no title or back control of its own: it sits inside the Notifications
   * settings workspace, which owns the heading, and the settings sidebar is
   * the way out. The office copy also shows the company daily alert; the field
   * copy never does.
   *
   * Keyed on the path rather than on the role: the path is what actually
   * distinguishes the two mounts, and an office admin who opens the field URL
   * gets the behaviour that URL implies.
   */
  const backToFieldHome = pathname.startsWith('/field');
  const queryClient = useQueryClient();
  const { user, isFieldWorker, hasVertical } = useAuth();
  // PATCH /company/alert-settings is non-strict requireRole('admin').
  const canWriteCompany = useCanWriteCompanySettings();
  const [prefs, setPrefs] = useState<PrefMap>({});
  const [saving, setSaving] = useState(false);

  // Only these rows/columns are rendered — and only these are submitted on save,
  // so the screen never writes a preference row for a control it didn't show.
  const visibleEvents = EVENT_TYPES.filter(
    e => !(isFieldWorker && FIELD_WORKER_HIDDEN_EVENTS.includes(e.key))
      && eventVisibleToRole(e, user?.role)
      && eventVisibleToVerticals(e, hasVertical),
  );
  const visibleChannels = CHANNELS.filter(ch => !HIDDEN_CHANNELS.includes(ch.key));

  // ─── Company daily alert (hour only) ──────────────────────────────
  // GET /company/alert-settings returns { time_zone, daily_alert_hour } and is
  // open to any tenant user. PATCH is requireRole('admin'): literal 'admin',
  // plus a dispatcher the tenant has elevated (useCanWriteCompanySettings).
  //
  // THE TIMEZONE IS NOT EDITED HERE. It is companies.time_zone, the column
  // Regional & Tax owns and invoices, pay periods and SMS all read. This page
  // used to carry a second control for it and sent it on EVERY save, so saving
  // the hour from a page loaded before a Regional & Tax change put the old zone
  // back. The PATCH now sends daily_alert_hour alone, and the zone is shown
  // read-only with a link to where it is set.
  //
  // Shown only on the office mount, and never to field roles: the alert goes
  // to admin, dispatcher and office_staff (daily-job-alerts.ts), and the field
  // route is a hand's only settings page.
  const showCompanySection = !backToFieldHome && !isFieldWorker;
  const [dailyAlertHour, setDailyAlertHour] = useState<number | null>(null);

  const {
    data: alertSettings,
    isLoading: alertLoading,
    isError: alertError,
    refetch: refetchAlert,
  } = useQuery({
    queryKey: ['company', 'alert-settings'],
    queryFn: () =>
      api.alertSettings.get().then(r => unwrap<{ time_zone: string | null; daily_alert_hour: number | null }>(r.data)),
    enabled: showCompanySection,
  });

  const savedHour = alertSettings?.daily_alert_hour ?? null;
  useEffect(() => {
    setDailyAlertHour(savedHour);
  }, [savedHour]);

  const alertDirty = dailyAlertHour != null && dailyAlertHour !== savedHour;

  const alertMutation = useMutation({
    mutationFn: (hour: number) => api.alertSettings.update({ daily_alert_hour: hour }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'alert-settings'] });
      toast.success('Daily alert time saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save the daily alert time'),
  });

  // ─── Morning brief hour (api#806) ────────────────────────────────
  // companies.brief_hour — when the in-app morning brief is written. Its own
  // column so moving the brief does not move the email above. Same audience
  // and write gate as the daily alert.
  const [briefHour, setBriefHour] = useState<number | null>(null);
  const {
    data: briefSettings,
    isLoading: briefLoading,
    isError: briefError,
    refetch: refetchBrief,
  } = useQuery({
    queryKey: ['company', 'brief-settings'],
    queryFn: () =>
      api.briefSettings.get().then(r => unwrap<{ brief_hour: number | null; time_zone: string | null }>(r.data)),
    enabled: showCompanySection,
  });
  const savedBriefHour = briefSettings?.brief_hour ?? null;
  useEffect(() => {
    setBriefHour(savedBriefHour);
  }, [savedBriefHour]);
  const briefDirty = briefHour != null && briefHour !== savedBriefHour;
  const briefMutation = useMutation({
    mutationFn: (hour: number) => api.briefSettings.update({ brief_hour: hour }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'brief-settings'] });
      toast.success('Morning brief time saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save the morning brief time'),
  });

  const timeZoneLabel = (tz: string | null | undefined): string => {
    if (!tz) return '—';
    // A zone outside the allowlist predates it; show the stored id rather
    // than a guessed label.
    return TIMEZONE_OPTIONS.find(o => o.value === tz)?.label ?? tz;
  };

  // ─── Notification preferences ─────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.notificationPreferences.get(),
  });

  useEffect(() => {
    if (data?.data?.data) {
      const map: PrefMap = {};
      for (const row of data.data.data) {
        const eventKey = row.event_type;
        if (!map[eventKey]) map[eventKey] = {};
        map[eventKey][row.channel] = row.enabled;
      }
      setPrefs(map);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (preferences: { channel: string; eventType: string; enabled: boolean }[]) => {
      return api.notificationPreferences.update(preferences);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success('Notification preferences saved');
    },
    // Show the API's own refusal. A generic toast hid "Invalid event_type:
    // casing_spec_corrected" from every office user on a casing tenant until
    // fieldiq-api #993 (2026-10-09) -- the one sentence that named the bug.
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save notification preferences'),
    onSettled: () => setSaving(false),
  });

  function toggle(eventType: string, channel: string) {
    setPrefs(prev => {
      const next = { ...prev };
      if (!next[eventType]) next[eventType] = {};
      const current = next[eventType][channel] ?? EVENT_DEFAULTS[eventType]?.[channel] ?? false;
      next[eventType] = { ...next[eventType], [channel]: !current };
      return next;
    });
  }

  function isEnabled(eventType: string, channel: string): boolean {
    return prefs[eventType]?.[channel] ?? EVENT_DEFAULTS[eventType]?.[channel] ?? false;
  }

  function handleSavePreferences() {
    setSaving(true);
    const preferences: { channel: string; eventType: string; enabled: boolean }[] = [];
    for (const event of visibleEvents) {
      for (const ch of visibleChannels) {
        if (!eventHasChannel(event, ch.key, user?.role)) continue;
        preferences.push({
          channel: ch.key,
          eventType: event.key,
          enabled: isEnabled(event.key, ch.key),
        });
      }
    }
    saveMutation.mutate(preferences);
  }

  if (isLoading) {
    return (
      /* Same split as the settled screen below: the fill belongs to the field
         mount, not to this screen. */
      <div className={`min-h-full flex items-center justify-center ${backToFieldHome ? 'bg-fieldiq-dark' : ''}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-fieldiq-accent" />
      </div>
    );
  }

  const selectCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50';

  return (
    /* THE FILL IS THE FIELD MOUNT'S, not this screen's. On /field this is a
       whole page and paints the page colour; in the Notifications settings
       workspace it sits inside the settings card panel, where painting the
       page colour would cut a grey rectangle through a white card in light
       mode. Same flag as the title and the back control above — the two mounts
       differ, and the path is what tells them apart. */
    <div className={`min-h-full pb-[var(--bottom-chrome)] text-white ${backToFieldHome ? 'bg-fieldiq-dark' : ''}`}>
      {/* The office mount sits inside the Notifications workspace, which owns
          the h1; the settings sidebar is the way out. The field mount has no
          workspace around it, so it keeps its own title and a back control
          that lands on /field. */}
      {backToFieldHome && (
        <div className="border-b border-border px-6 py-4">
          <button onClick={goBack} aria-label="Back" className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
            <ArrowLeft size={24} /> Back
          </button>
          <div className="flex items-center justify-between mt-2">
            <h1 className="text-base font-bold text-foreground">Notification Preferences</h1>
          </div>
        </div>
      )}

      <div className="px-6 py-4 space-y-8">
        {/* ─── This device: push on/off, a test, and (office) today's daily alert ── */}
        <PushDeviceCard
          showDailyAlertTest={showCompanySection && ['admin', 'dispatcher', 'office_staff'].includes(user?.role) && hasVertical('casing_running')}
        />

        {/* ─── Personal: this user's own channel × event choices ─────── */}
        <section aria-labelledby="notif-personal-heading">
          <div className="flex items-center justify-between gap-4 mb-1">
            <h2 id="notif-personal-heading" className="text-sm font-semibold text-white">Your notifications</h2>
            <button
              onClick={handleSavePreferences}
              disabled={saving}
              className="bg-fieldiq-accent text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Only you. Choose how you want to be notified for each event — everyone on the team sets their own.
          </p>

          {/* Info banner for SMS */}
          <div className="mb-4 bg-blue-900/30 border border-blue-700/50 rounded-lg px-4 py-3 text-sm text-blue-300">
            SMS notifications require a phone number on your profile. Where Push is on and this {deviceKind()} has alerts on, you get the push instead of the text; the text is the fallback.
          </div>

          {/* Column headers */}
          <div className="flex items-center mb-3 pl-4">
            <div className="flex-1" />
            {visibleChannels.map(ch => (
              <div key={ch.key} className="w-16 text-center text-xs text-gray-400 font-medium">
                {ch.label}
              </div>
            ))}
          </div>

          {/* A tenant whose verticals emit nothing this person receives (a
              pressure-testing field tech, today) gets a sentence, not a
              header row over an empty table. */}
          {visibleEvents.length === 0 && (
            <p className="text-sm text-fieldiq-muted bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3">
              There are no notifications to set up for your work yet.
            </p>
          )}

          {/* Rows */}
          <div className="space-y-2">
            {visibleEvents.map(event => (
              <div
                key={event.key}
                className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 flex items-center"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white">{event.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{event.description}</div>
                </div>
                {visibleChannels.map(ch => (
                  <div key={ch.key} className="w-16 flex justify-center">
                    {!eventHasChannel(event, ch.key, user?.role) ? (
                      <span className="text-gray-500 text-sm" aria-label={`${ch.label} not available for ${event.label}`}>—</span>
                    ) : (
                      <button
                        onClick={() => toggle(event.key, ch.key)}
                        aria-label={`${event.label} via ${ch.label}`}
                        aria-pressed={isEnabled(event.key, ch.key)}
                        className={`w-10 h-5 rounded-full transition-colors duration-200 relative ${
                          isEnabled(event.key, ch.key) ? 'bg-fieldiq-accent' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200 ${
                            isEnabled(event.key, ch.key) ? 'translate-x-[20px]' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* ─── Company: the daily alert, one setting for the whole tenant ── */}
        {showCompanySection && (
          <section aria-labelledby="notif-company-heading">
            <h2 id="notif-company-heading" className="text-sm font-semibold text-white mb-1">Company daily alert</h2>
            <p className="text-xs text-gray-400 mb-4">
              Applies to the whole company. This is the email: the daily summary of unstarted jobs goes to admins, dispatchers and office staff at this time (and by text to anyone who has opted in).
            </p>

            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-4 max-w-2xl">
              {alertLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  <Loader2 size={20} className="animate-spin" />
                </div>
              ) : alertError ? (
                /* A failed read is a refusal, not an empty state: no controls
                   over values that could not be fetched. */
                <div role="alert" className="flex items-center justify-between gap-3 text-sm text-red-300">
                  <span>Could not load the daily alert settings.</span>
                  <button onClick={() => refetchAlert()} className="px-3 py-1.5 rounded-lg border border-red-400/40 text-xs font-semibold hover:bg-red-500/10">Retry</button>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="daily_alert_hour" className="text-sm font-medium text-white block mb-1">
                      Send time
                    </label>
                    {canWriteCompany ? (
                      <select
                        id="daily_alert_hour"
                        value={dailyAlertHour ?? ''}
                        onChange={e => setDailyAlertHour(Number(e.target.value))}
                        className={selectCls}
                      >
                        {/* A select cannot show a value it has no option for, so
                            an unset hour gets one rather than displaying 12 AM. */}
                        {dailyAlertHour == null && <option value="" disabled>—</option>}
                        {HOURS.map(h => (
                          <option key={h} value={h}>{formatHour(h)}</option>
                        ))}
                      </select>
                    ) : (
                      <p id="daily_alert_hour" className="text-sm text-white" data-testid="alert-send-time">
                        {savedHour == null ? '—' : formatHour(savedHour)}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="text-sm font-medium text-white mb-1">Time zone</div>
                    <p className="text-sm text-white" data-testid="alert-time-zone">{timeZoneLabel(alertSettings?.time_zone)}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      The company time zone. Set in{' '}
                      <Link to="/settings/regional-tax" className="text-fieldiq-accent hover:underline">Finance &amp; region → Regional &amp; Tax</Link>.
                    </p>
                  </div>

                  {canWriteCompany ? (
                    <button
                      onClick={() => dailyAlertHour != null && alertMutation.mutate(dailyAlertHour)}
                      disabled={!alertDirty || alertMutation.isPending}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        alertDirty
                          ? 'bg-fieldiq-accent text-white hover:bg-fieldiq-accent/90'
                          : 'bg-muted text-fieldiq-muted cursor-not-allowed'
                      }`}
                    >
                      {alertMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                      {alertMutation.isPending ? 'Saving...' : 'Save send time'}
                    </button>
                  ) : (
                    <p className="text-xs text-fieldiq-muted">Only an admin can change the daily alert.</p>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {/* ─── Company: the morning brief hour, beside the daily alert ── */}
        {showCompanySection && (
          <section aria-labelledby="notif-brief-heading">
            <h2 id="notif-brief-heading" className="text-sm font-semibold text-white mb-1">Morning brief time</h2>
            <p className="text-xs text-gray-400 mb-4">
              Applies to the whole company. The morning brief is in-app only: admins and office staff see it on Home and in the FieldIQ Agent. It is not emailed. The email is the company daily alert above.
            </p>

            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-4 max-w-2xl">
              {briefLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  <Loader2 size={20} className="animate-spin" />
                </div>
              ) : briefError ? (
                <div role="alert" className="flex items-center justify-between gap-3 text-sm text-red-300">
                  <span>Could not load the morning brief time.</span>
                  <button onClick={() => refetchBrief()} className="px-3 py-1.5 rounded-lg border border-red-400/40 text-xs font-semibold hover:bg-red-500/10">Retry</button>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="brief_hour" className="text-sm font-medium text-white block mb-1">
                      Ready by
                    </label>
                    {canWriteCompany ? (
                      <select
                        id="brief_hour"
                        value={briefHour ?? ''}
                        onChange={e => setBriefHour(Number(e.target.value))}
                        className={selectCls}
                      >
                        {briefHour == null && <option value="" disabled>—</option>}
                        {HOURS.map(h => (
                          <option key={h} value={h}>{formatHour(h)}</option>
                        ))}
                      </select>
                    ) : (
                      <p id="brief_hour" className="text-sm text-white" data-testid="brief-hour">
                        {savedBriefHour == null ? '—' : formatHour(savedBriefHour)}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">In the company time zone, {timeZoneLabel(briefSettings?.time_zone)}.</p>
                  </div>

                  {canWriteCompany ? (
                    <button
                      onClick={() => briefHour != null && briefMutation.mutate(briefHour)}
                      disabled={!briefDirty || briefMutation.isPending}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        briefDirty
                          ? 'bg-fieldiq-accent text-white hover:bg-fieldiq-accent/90'
                          : 'bg-muted text-fieldiq-muted cursor-not-allowed'
                      }`}
                    >
                      {briefMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                      {briefMutation.isPending ? 'Saving...' : 'Save brief time'}
                    </button>
                  ) : (
                    <p className="text-xs text-fieldiq-muted">Only an admin can change the morning brief time.</p>
                  )}
                </>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
